import React, { useState, useEffect } from 'react';
import { AnimatedSection } from '../../../lib/animations/AnimatedSection';

function useCountdown(targetDate) {
  const [tl, setTl] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    if (!targetDate) return;
    const tick = () => {
      const diff = Math.max(0, new Date(targetDate).getTime() - Date.now());
      setTl({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return tl;
}

export default function CountdownCentered({ section, data, theme }) {
  const anim = section.animation || {};
  const countdown = useCountdown(data.targetDateTime);
  if (!data.targetDateTime) return null;

  // Derive dark mode from bg luminance
  const isDark = isColorDark(theme.bg);
  const cardBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)';
  const borderC = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

  return (
    <section className="px-5 pb-20" data-testid="countdown-section">
      <div className="max-w-sm mx-auto text-center">
        <AnimatedSection preset={anim.section || 'fade-in'}>
          <p
            className="text-xs tracking-[0.3em] uppercase mb-8"
            style={{ color: theme.accent, fontFamily: theme.eyebrowFont }}
          >
            {data.label || 'Counting Down'}
          </p>
        </AnimatedSection>
        <AnimatedSection preset={anim.section || 'fade-in'} delay={0.1}>
          <div className="grid grid-cols-4 gap-3">
            {[
              { val: countdown.days, label: 'Days' },
              { val: countdown.hours, label: 'Hours' },
              { val: countdown.minutes, label: 'Minutes' },
              { val: countdown.seconds, label: 'Seconds' },
            ].map(({ val, label }) => (
              <div
                key={label}
                className="rounded-2xl py-4 px-2"
                style={{ background: cardBg, border: `1px solid ${borderC}` }}
              >
                <div
                  className="text-2xl sm:text-3xl font-light tabular-nums"
                  style={{ color: theme.textPrimary, fontFamily: theme.headingFont }}
                >
                  {String(val).padStart(2, '0')}
                </div>
                <div
                  className="text-[10px] tracking-wider uppercase mt-1"
                  style={{ color: theme.textSecondary, fontFamily: theme.eyebrowFont }}
                >
                  {label}
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

function isColorDark(hex) {
  if (!hex) return false;
  const c = hex.replace('#', '');
  if (c.length < 6) return false;
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}
